import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Referral,
  ReferralFormData,
  Provider,
} from '../types/referral'
import { referralService } from '../services/referralService'
import { providerService } from '../services/providerService'
import { patientService } from '../services/patientService'
import { consultationService } from '../services/consultationService'
import { templateService } from '../services/templateService'
import { patientFileService } from '../services/patientFileService'
import { Accordion, AccordionSectionConfig } from './Accordion'
import { formatPatientName } from '../utils/name'
import './ReferralForm.css'

const referralSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  consultationId: z.string().optional(),
  referredToProviderId: z.string().optional(),
  referredToProviderName: z.string().min(1, 'Provider name is required'),
  referredToProviderType: z.enum(['internal', 'external']).optional(),
  referralDate: z.string().min(1, 'Referral date is required'),
  priority: z.enum(['routine', 'urgent', 'emergency']),
  status: z.enum(['draft', 'sent', 'acknowledged', 'completed', 'cancelled']).optional(),
  reason: z.string().min(1, 'Reason for referral is required'),
  templateId: z.string().optional(),
  communicationMethod: z.enum(['healthmail', 'email', 'print', 'manual', 'pending']),
  followUpRequired: z.boolean().optional(),
  followUpDate: z.string().optional(),
  notes: z.string().optional(),
})

interface ReferralFormProps {
  patientId: string
  consultationId?: string
  referral?: Referral
  onSuccess?: () => void
  onCancel?: () => void
}

export function ReferralForm({
  patientId,
  consultationId,
  referral,
  onSuccess,
  onCancel,
}: ReferralFormProps) {
  const queryClient = useQueryClient()
  const isEditing = !!referral

  const createSafeFileName = (baseName: string) => {
    return baseName
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 150)
  }

  const base64ToFile = (base64: string, fileName: string, mimeType?: string) => {
    const byteCharacters = atob(base64.trim())
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: mimeType || 'application/vnd.oasis.opendocument.text' })
    return new File([blob], fileName, { type: mimeType || 'application/vnd.oasis.opendocument.text' })
  }

  const buildReferralSummary = (savedReferral: Referral) => {
    return [
      'Referral Summary',
      '',
      `Patient: ${patientDisplayName}`,
      `Referral ID: ${savedReferral.id}`,
      `Date: ${savedReferral.referralDate || ''}`,
      `Status: ${savedReferral.status || ''}`,
      `Priority: ${savedReferral.priority || ''}`,
      `Communication Method: ${savedReferral.communicationMethod || ''}`,
      '',
      `Referred To: ${savedReferral.referredToProviderName || ''}`,
      `Provider Specialty: ${savedReferral.providerDetails?.specialty || ''}`,
      '',
      `Reason: ${savedReferral.reason || ''}`,
      '',
      `Notes: ${savedReferral.notes || ''}`,
    ].join('\n')
  }

  // Fetch patient data
  const { data: patient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId),
    enabled: !!patientId,
  })
  const patientDisplayName = patient ? formatPatientName(patient as any) : 'patient'

  // Fetch consultation data if provided
  const { data: consultation } = useQuery({
    queryKey: ['consultation', consultationId],
    queryFn: () => consultationService.getById(consultationId!),
    enabled: !!consultationId,
  })

  // Fetch providers
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: () => providerService.getAll({ isActive: true }),
  })

  // Fetch templates (referral category)
  const { data: templates } = useQuery({
    queryKey: ['templates', 'referral'],
    queryFn: () => templateService.getAll('referral'),
  })

  const [useProviderDirectory, setUseProviderDirectory] = useState(true)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
  const [providerSearchTerm, setProviderSearchTerm] = useState('')
  const [showProviderResults, setShowProviderResults] = useState(false)
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(-1)
  const [providerResultsPosition, setProviderResultsPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const providerInputRef = useRef<HTMLInputElement>(null)
  const providerResultsRef = useRef<HTMLDivElement>(null)
  const [adHocProvider, setAdHocProvider] = useState({
    name: '',
    organization: '',
    specialty: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Ireland',
    },
    phone: '',
    email: '',
    healthmailAddress: '',
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ReferralFormData>({
    resolver: zodResolver(referralSchema),
    defaultValues: {
      patientId,
      consultationId: consultationId || referral?.consultationId,
      referralDate: referral?.referralDate || new Date().toISOString().split('T')[0],
      priority: referral?.priority || 'routine',
      status: referral?.status || 'draft',
      reason: referral?.reason || '',
      referredToProviderName: referral?.referredToProviderName || '',
      referredToProviderType: referral?.referredToProviderType || 'external',
      templateId: referral?.templateId,
      communicationMethod: referral?.communicationMethod || 'pending',
      followUpRequired: referral?.followUpRequired || false,
      followUpDate: referral?.followUpDate,
      notes: referral?.notes,
      clinicalDetails: referral?.clinicalDetails || {
        presentingComplaint: consultation?.reason || '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: '',
        currentMedications: [],
        allergies: [],
        examinationFindings: '',
        investigations: '',
        diagnosis: consultation?.diagnosis || [],
        treatmentToDate: consultation?.plan || '',
        specificQuestions: '',
      },
      insuranceInformation: referral?.insuranceInformation || {
        insuranceProvider: patient?.insurance?.provider,
        policyNumber: patient?.insurance?.policyNumber,
        groupNumber: patient?.insurance?.groupNumber,
      },
    },
  })

  // Pre-populate from consultation if available
  useEffect(() => {
    if (consultation && !isEditing) {
      const clinicalDetails = watch('clinicalDetails') || {}
      setValue('clinicalDetails', {
        ...clinicalDetails,
        presentingComplaint: consultation.reason || clinicalDetails.presentingComplaint || '',
        diagnosis: consultation.diagnosis || clinicalDetails.diagnosis || [],
        treatmentToDate: consultation.plan || clinicalDetails.treatmentToDate || '',
        historyOfPresentingComplaint: consultation.notes || clinicalDetails.historyOfPresentingComplaint || '',
      })
    }
  }, [consultation, isEditing, setValue, watch])

  // Pre-populate allergies and medications from patient
  useEffect(() => {
    if (patient && !isEditing) {
      const clinicalDetails = watch('clinicalDetails') || {}
      setValue('clinicalDetails', {
        ...clinicalDetails,
        allergies: patient.allergies || clinicalDetails.allergies || [],
        currentMedications: patient.currentMedications || clinicalDetails.currentMedications || [],
      })
    }
  }, [patient, isEditing, setValue, watch])

  // Update provider results position for fixed positioning (to escape accordion overflow)
  const updateProviderResultsPosition = useCallback(() => {
    if (providerInputRef.current) {
      const rect = providerInputRef.current.getBoundingClientRect()
      const position = {
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      }
      setProviderResultsPosition(position)
      return position
    }
    return null
  }, [])

  // Filter providers based on search term
  const filteredProviders = useMemo(() => {
    if (!providers || !providerSearchTerm.trim()) {
      return []
    }
    
    const search = providerSearchTerm.toLowerCase().trim()
    return providers.filter((p) => {
      const displayName = (p.fullName || p.name || '').trim()
      const name = (p.title ? `${p.title} ` : '') + displayName
      return (
        name.toLowerCase().includes(search) ||
        p.organization?.toLowerCase().includes(search) ||
        p.specialty?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.phone?.includes(search)
      )
    }).slice(0, 20) // Limit to 20 results
  }, [providers, providerSearchTerm])

  // Update position when search term changes and results should show
  useEffect(() => {
    if (providerSearchTerm.trim() && filteredProviders.length > 0 && showProviderResults) {
      updateProviderResultsPosition()
    }
  }, [providerSearchTerm, filteredProviders.length, showProviderResults, updateProviderResultsPosition])

  // Handle provider selection
  const handleProviderSelect = useCallback((provider: Provider) => {
    const providerName = provider.fullName || provider.name
    setSelectedProvider(provider)
    setProviderSearchTerm(`${provider.title ? `${provider.title} ` : ''}${providerName}${provider.organization ? ` - ${provider.organization}` : ''}${provider.specialty ? ` (${provider.specialty})` : ''}`)
    setShowProviderResults(false)
    setValue('referredToProviderId', provider.id)
    setValue('referredToProviderName', providerName)
    setValue('referredToProviderType', provider.type)
    setValue('providerDetails', {
      name: providerName,
      firstName: provider.firstName,
      lastName: provider.lastName,
      fullName: providerName,
      title: provider.title,
      organization: provider.organization,
      specialty: provider.specialty,
      address: provider.address,
      phone: provider.phone,
      email: provider.email,
      healthmailAddress: provider.healthmailAddress,
    })
  }, [setValue])

  // Handle keyboard navigation in provider search
  useEffect(() => {
    if (!showProviderResults || filteredProviders.length === 0) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedProviderIndex((prev) =>
          prev < filteredProviders.length - 1 ? prev + 1 : prev
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedProviderIndex((prev) => (prev > 0 ? prev - 1 : 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredProviders[selectedProviderIndex]) {
          handleProviderSelect(filteredProviders[selectedProviderIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setShowProviderResults(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showProviderResults, filteredProviders, selectedProviderIndex, handleProviderSelect])

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        providerInputRef.current &&
        !providerInputRef.current.contains(event.target as Node) &&
        providerResultsRef.current &&
        !providerResultsRef.current.contains(event.target as Node)
      ) {
        setShowProviderResults(false)
      }
    }

    if (showProviderResults) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showProviderResults])

  // Reset selected index when search term changes
  useEffect(() => {
    setSelectedProviderIndex(0)
  }, [providerSearchTerm])

  // Initialize search term when provider is selected (for editing)
  useEffect(() => {
    if (referral?.referredToProviderId && providers && !providerSearchTerm && !selectedProvider) {
      const provider = providers.find((p) => p.id === referral.referredToProviderId)
      if (provider) {
        setSelectedProvider(provider)
        const providerName = provider.fullName || provider.name
        setProviderSearchTerm(`${provider.title ? `${provider.title} ` : ''}${providerName}${provider.organization ? ` - ${provider.organization}` : ''}${provider.specialty ? ` (${provider.specialty})` : ''}`)
      }
    } else if (referral?.referredToProviderName && !providerSearchTerm && !selectedProvider) {
      // If we have a provider name but no ID, just use the name
      setProviderSearchTerm(referral.referredToProviderName)
    }
  }, [referral, providers, providerSearchTerm, selectedProvider])

  // Create/update referral mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ReferralFormData) => {
      if (isEditing && referral) {
        return referralService.update(referral.id, data)
      } else {
        return referralService.create(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] })
      queryClient.invalidateQueries({ queryKey: ['referrals', patientId] })
      onSuccess?.()
    },
  })

  const onSubmit = async (data: ReferralFormData) => {
    // Build provider details
    let providerDetails = data.providerDetails
    if (useProviderDirectory && selectedProvider) {
      const providerName = selectedProvider.fullName || selectedProvider.name
      providerDetails = {
        name: providerName,
        firstName: selectedProvider.firstName,
        lastName: selectedProvider.lastName,
        fullName: providerName,
        title: selectedProvider.title,
        organization: selectedProvider.organization,
        specialty: selectedProvider.specialty,
        address: selectedProvider.address,
        phone: selectedProvider.phone,
        email: selectedProvider.email,
        healthmailAddress: selectedProvider.healthmailAddress,
      }
    } else {
      providerDetails = {
        name: adHocProvider.name,
        organization: adHocProvider.organization || undefined,
        specialty: adHocProvider.specialty || undefined,
        address: adHocProvider.address.street ? adHocProvider.address : undefined,
        phone: adHocProvider.phone || undefined,
        email: adHocProvider.email || undefined,
        healthmailAddress: adHocProvider.healthmailAddress || undefined,
      }
    }

    // Process clinical details - convert textarea strings to arrays where needed
    const clinicalDetails = data.clinicalDetails || {}
    const processedClinicalDetails = {
      ...clinicalDetails,
      currentMedications: Array.isArray(clinicalDetails.currentMedications)
        ? clinicalDetails.currentMedications
        : (clinicalDetails.currentMedications ? String(clinicalDetails.currentMedications).split('\n').filter(Boolean) : []),
      allergies: Array.isArray(clinicalDetails.allergies)
        ? clinicalDetails.allergies
        : (clinicalDetails.allergies ? String(clinicalDetails.allergies).split('\n').filter(Boolean) : []),
      diagnosis: Array.isArray(clinicalDetails.diagnosis)
        ? clinicalDetails.diagnosis
        : (clinicalDetails.diagnosis ? String(clinicalDetails.diagnosis).split('\n').filter(Boolean) : []),
    }

    const trimmedTemplateId = data.templateId?.trim()
    const selectedTemplate = trimmedTemplateId
      ? templates?.find((template) => template.id === trimmedTemplateId)
      : undefined
    const referralData: ReferralFormData = {
      ...data,
      templateName: selectedTemplate?.name,
      providerDetails,
      referredToProviderName: providerDetails.name,
      clinicalDetails: processedClinicalDetails,
    }

    const savedReferral = await saveMutation.mutateAsync(referralData)

    if (!isEditing && savedReferral) {
      try {
        if (trimmedTemplateId) {
          try {
            const referralAllergies = processedClinicalDetails.allergies || []
            const normalizedAllergies = Array.isArray(referralAllergies)
              ? referralAllergies.map((item) => String(item).trim()).filter(Boolean)
              : String(referralAllergies).split(',').map((item) => item.trim()).filter(Boolean)
            const allergiesList = normalizedAllergies.join(', ')
            const response = await templateService.generateDocument({
              templateId: trimmedTemplateId,
              patientId,
              consultationId: savedReferral.consultationId,
              referralId: savedReferral.id,
              customVariables: allergiesList
                ? { patient: { allergies: normalizedAllergies, allergiesList } }
                : undefined,
            })
            const file = base64ToFile(response.content, response.fileName, response.mimeType)
            await patientFileService.upload(patientId, {
              file,
              displayName: response.fileName,
              description: selectedTemplate?.name
                ? `Referral document generated from template: ${selectedTemplate.name}`
                : 'Referral document generated from template',
              category: 'referral',
            })
            try {
              templateService.downloadDocument(response.content, response.fileName, response.mimeType)
            } catch (downloadError) {
              console.error('[ReferralForm] Referral download failed:', downloadError)
            }
          } catch (error) {
            console.error('[ReferralForm] Referral document generation failed, falling back to summary:', error)
            const dateStamp = savedReferral.referralDate || new Date().toISOString().split('T')[0]
            const safeName = createSafeFileName(`Referral_${patientDisplayName}_${dateStamp}`)
            const fileName = `${safeName}.txt`
            const summaryFile = new File([buildReferralSummary(savedReferral)], fileName, { type: 'text/plain' })
            await patientFileService.upload(patientId, {
              file: summaryFile,
              displayName: fileName,
              description: 'Referral summary (template generation failed)',
              category: 'referral',
            })
          }
        } else {
          const dateStamp = savedReferral.referralDate || new Date().toISOString().split('T')[0]
          const safeName = createSafeFileName(`Referral_${patientDisplayName}_${dateStamp}`)
          const fileName = `${safeName}.txt`
          const summaryFile = new File([buildReferralSummary(savedReferral)], fileName, { type: 'text/plain' })
          await patientFileService.upload(patientId, {
            file: summaryFile,
            displayName: fileName,
            description: 'Referral summary',
            category: 'referral',
          })
        }

        queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
        queryClient.invalidateQueries({ queryKey: ['patient-files', 'patient', patientId] })
      } catch (error) {
        console.error('[ReferralForm] Failed to save referral file:', error)
        alert('Referral saved, but adding it to patient files failed. Please try again.')
      }
    }
  }

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'provider':
        return (
          <div className="referral-form-section">
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="providerSource"
                  checked={useProviderDirectory}
                  onChange={() => setUseProviderDirectory(true)}
                />
                Select from Provider Directory
              </label>
              <label>
                <input
                  type="radio"
                  name="providerSource"
                  checked={!useProviderDirectory}
                  onChange={() => setUseProviderDirectory(false)}
                />
                Enter Provider Details Manually
              </label>
            </div>

            {useProviderDirectory ? (
              <div className="form-group">
                <label htmlFor="referredToProviderId">
                  Provider <span className="required">*</span>
                </label>
                <div className="provider-search-wrapper" ref={providerInputRef}>
                  <input
                    id="referredToProviderId"
                    type="text"
                    value={providerSearchTerm}
                    onChange={(e) => {
                      const value = e.target.value
                      setProviderSearchTerm(value)
                      if (value.trim()) {
                        // Calculate position immediately (synchronously) to show dropdown
                        if (providerInputRef.current) {
                          const rect = providerInputRef.current.getBoundingClientRect()
                          setProviderResultsPosition({
                            top: rect.bottom + 4,
                            left: rect.left,
                            width: rect.width,
                          })
                        }
                        setShowProviderResults(true)
                      } else {
                        setShowProviderResults(false)
                        setSelectedProvider(null)
                        setValue('referredToProviderId', '')
                        setValue('referredToProviderName', '')
                        setProviderResultsPosition(null)
                      }
                    }}
                    onFocus={() => {
                      if (providerSearchTerm.trim()) {
                        updateProviderResultsPosition()
                        setShowProviderResults(true)
                      }
                    }}
                    placeholder="Search for provider by name, organization, or specialty..."
                    className={errors.referredToProviderId ? 'error' : ''}
                    autoComplete="off"
                  />
                  {showProviderResults && filteredProviders.length > 0 && (
                    <div 
                      className="provider-search-results" 
                      ref={providerResultsRef}
                      style={providerResultsPosition ? {
                        position: 'fixed',
                        top: `${providerResultsPosition.top}px`,
                        left: `${providerResultsPosition.left}px`,
                        width: `${providerResultsPosition.width}px`,
                      } : {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                      }}
                    >
                      {filteredProviders.map((provider, index) => (
                        <button
                          key={provider.id}
                          type="button"
                          className={`provider-search-result ${index === selectedProviderIndex ? 'selected' : ''}`}
                          onClick={() => handleProviderSelect(provider)}
                        >
                          <div className="provider-search-name">
                            {provider.title ? `${provider.title} ` : ''}
                            {provider.name}
                          </div>
                          {provider.organization && (
                            <div className="provider-search-organization">{provider.organization}</div>
                          )}
                          {provider.specialty && (
                            <div className="provider-search-specialty">{provider.specialty}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {showProviderResults && providerSearchTerm.trim() && filteredProviders.length === 0 && (
                    <div 
                      className="provider-search-results"
                      style={providerResultsPosition ? {
                        position: 'fixed',
                        top: `${providerResultsPosition.top}px`,
                        left: `${providerResultsPosition.left}px`,
                        width: `${providerResultsPosition.width}px`,
                      } : {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                      }}
                    >
                      <div className="provider-search-no-results">No providers found</div>
                    </div>
                  )}
                </div>
                {errors.referredToProviderId && (
                  <span className="error-message" role="alert">
                    {errors.referredToProviderId.message}
                  </span>
                )}
                {errors.referredToProviderName && (
                  <span className="error-message" role="alert">
                    {errors.referredToProviderName.message}
                  </span>
                )}
                {!selectedProvider && providerSearchTerm.trim() && filteredProviders.length === 0 && providerSearchTerm.length > 2 && (
                  <small>No matching provider found. You can enter provider details manually by selecting "Enter Provider Details Manually" above.</small>
                )}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="adHocProviderName">
                    Provider Name <span className="required">*</span>
                  </label>
                  <input
                    id="adHocProviderName"
                    type="text"
                    value={adHocProvider.name}
                    onChange={(e) => {
                      setAdHocProvider({ ...adHocProvider, name: e.target.value })
                      setValue('referredToProviderName', e.target.value)
                    }}
                    required
                  />
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label htmlFor="adHocProviderOrganization">Organization</label>
                    <input
                      id="adHocProviderOrganization"
                      type="text"
                      value={adHocProvider.organization}
                      onChange={(e) =>
                        setAdHocProvider({ ...adHocProvider, organization: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="adHocProviderSpecialty">Specialty</label>
                    <input
                      id="adHocProviderSpecialty"
                      type="text"
                      value={adHocProvider.specialty}
                      onChange={(e) =>
                        setAdHocProvider({ ...adHocProvider, specialty: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="adHocProviderAddress">Street Address</label>
                  <input
                    id="adHocProviderAddress"
                    type="text"
                    placeholder="Street address"
                    value={adHocProvider.address.street}
                    onChange={(e) =>
                      setAdHocProvider({
                        ...adHocProvider,
                        address: { ...adHocProvider.address, street: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="adHocProviderCity">City</label>
                    <input
                      id="adHocProviderCity"
                      type="text"
                      placeholder="City"
                      value={adHocProvider.address.city}
                      onChange={(e) =>
                        setAdHocProvider({
                          ...adHocProvider,
                          address: { ...adHocProvider.address, city: e.target.value },
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="adHocProviderCounty">County</label>
                    <input
                      id="adHocProviderCounty"
                      type="text"
                      placeholder="County"
                      value={adHocProvider.address.state}
                      onChange={(e) =>
                        setAdHocProvider({
                          ...adHocProvider,
                          address: { ...adHocProvider.address, state: e.target.value },
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="adHocProviderEircode">Eircode</label>
                    <input
                      id="adHocProviderEircode"
                      type="text"
                      placeholder="Eircode"
                      value={adHocProvider.address.postalCode}
                      onChange={(e) =>
                        setAdHocProvider({
                          ...adHocProvider,
                          address: { ...adHocProvider.address, postalCode: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="adHocProviderPhone">Phone</label>
                    <input
                      id="adHocProviderPhone"
                      type="tel"
                      value={adHocProvider.phone}
                      onChange={(e) =>
                        setAdHocProvider({ ...adHocProvider, phone: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="adHocProviderEmail">Email</label>
                    <input
                      id="adHocProviderEmail"
                      type="email"
                      value={adHocProvider.email}
                      onChange={(e) =>
                        setAdHocProvider({ ...adHocProvider, email: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="adHocProviderHealthmail">Healthmail Address</label>
                    <input
                      id="adHocProviderHealthmail"
                      type="email"
                      value={adHocProvider.healthmailAddress}
                      onChange={(e) =>
                        setAdHocProvider({ ...adHocProvider, healthmailAddress: e.target.value })
                      }
                      placeholder="provider@healthmail.ie"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )
      case 'referral-details':
        return (
          <div className="referral-form-section">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label htmlFor="referralDate">
                  Referral Date <span className="required">*</span>
                </label>
                <input
                  id="referralDate"
                  type="date"
                  {...register('referralDate')}
                  className={errors.referralDate ? 'error' : ''}
                />
                {errors.referralDate && (
                  <span className="error-message" role="alert">
                    {errors.referralDate.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="priority">
                  Priority <span className="required">*</span>
                </label>
                <select 
                  id="priority" 
                  {...register('priority')}
                  className={errors.priority ? 'error' : ''}
                >
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent</option>
                  <option value="emergency">Emergency</option>
                </select>
                {errors.priority && (
                  <span className="error-message" role="alert">
                    {errors.priority.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="communicationMethod">Communication Method</label>
                <select id="communicationMethod" {...register('communicationMethod')}>
                  <option value="pending">Pending</option>
                  <option value="healthmail">Healthmail</option>
                  <option value="email">Email</option>
                  <option value="print">Print</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="reason">
                  Reason for Referral <span className="required">*</span>
                </label>
                <input
                  id="reason"
                  type="text"
                  {...register('reason')}
                  placeholder="e.g., Chest pain evaluation, Dermatology consultation"
                  className={errors.reason ? 'error' : ''}
                />
                {errors.reason && (
                  <span className="error-message" role="alert">
                    {errors.reason.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="templateId">Template</label>
                <select id="templateId" {...register('templateId')}>
                  <option value="">No template</option>
                  {templates?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )
      case 'clinical-details':
        return (
          <div className="referral-form-section">
            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="presentingComplaint">Presenting Complaint</label>
                <textarea
                  id="presentingComplaint"
                  {...register('clinicalDetails.presentingComplaint')}
                  rows={4}
                  placeholder="Brief description of the presenting complaint"
                />
              </div>

              <div className="form-group">
                <label htmlFor="historyOfPresentingComplaint">History of Presenting Complaint</label>
                <textarea
                  id="historyOfPresentingComplaint"
                  {...register('clinicalDetails.historyOfPresentingComplaint')}
                  rows={4}
                  placeholder="Detailed history of the complaint"
                />
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="pastMedicalHistory">Past Medical History</label>
                <textarea
                  id="pastMedicalHistory"
                  {...register('clinicalDetails.pastMedicalHistory')}
                  rows={4}
                  placeholder="Relevant past medical history"
                />
              </div>

              <div className="form-group">
                <label htmlFor="examinationFindings">Examination Findings</label>
                <textarea
                  id="examinationFindings"
                  {...register('clinicalDetails.examinationFindings')}
                  rows={4}
                  placeholder="Relevant examination findings"
                />
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="currentMedications">Current Medications</label>
                <textarea
                  id="currentMedications"
                  value={Array.isArray(watch('clinicalDetails')?.currentMedications)
                    ? (watch('clinicalDetails')?.currentMedications || []).join('\n')
                    : (watch('clinicalDetails')?.currentMedications || '')}
                  onChange={(e) => {
                    const value = e.target.value
                    const medications = value ? value.split('\n').filter(Boolean) : []
                    setValue('clinicalDetails.currentMedications', medications)
                  }}
                  rows={3}
                  placeholder="List current medications (one per line)"
                />
                <small>Enter one medication per line</small>
              </div>

              <div className="form-group">
                <label htmlFor="allergies">Allergies</label>
                <textarea
                  id="allergies"
                  value={Array.isArray(watch('clinicalDetails')?.allergies)
                    ? (watch('clinicalDetails')?.allergies || []).join('\n')
                    : (watch('clinicalDetails')?.allergies || '')}
                  onChange={(e) => {
                    const value = e.target.value
                    const allergies = value ? value.split('\n').filter(Boolean) : []
                    setValue('clinicalDetails.allergies', allergies)
                  }}
                  rows={3}
                  placeholder="List known allergies (one per line)"
                />
                <small>Enter one allergy per line</small>
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="investigations">Investigations</label>
                <textarea
                  id="investigations"
                  {...register('clinicalDetails.investigations')}
                  rows={4}
                  placeholder="Investigations performed and results"
                />
              </div>

              <div className="form-group">
                <label htmlFor="diagnosis">Diagnosis</label>
                <textarea
                  id="diagnosis"
                  value={Array.isArray(watch('clinicalDetails')?.diagnosis)
                    ? (watch('clinicalDetails')?.diagnosis || []).join('\n')
                    : (watch('clinicalDetails')?.diagnosis || '')}
                  onChange={(e) => {
                    const value = e.target.value
                    const diagnoses = value ? value.split('\n').filter(Boolean) : []
                    setValue('clinicalDetails.diagnosis', diagnoses)
                  }}
                  rows={4}
                  placeholder="Diagnosis or differential diagnosis (one per line)"
                />
                <small>Enter one diagnosis per line</small>
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="treatmentToDate">Treatment to Date</label>
                <textarea
                  id="treatmentToDate"
                  {...register('clinicalDetails.treatmentToDate')}
                  rows={4}
                  placeholder="Treatment provided to date"
                />
              </div>

              <div className="form-group">
                <label htmlFor="specificQuestions">Specific Questions for Receiving Provider</label>
                <textarea
                  id="specificQuestions"
                  {...register('clinicalDetails.specificQuestions')}
                  rows={4}
                  placeholder="Specific questions or requests for the receiving provider"
                />
              </div>
            </div>
          </div>
        )
      case 'insurance':
        return (
          <div className="referral-form-section">
            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="insuranceProvider">Insurance Provider</label>
                <input
                  id="insuranceProvider"
                  type="text"
                  {...register('insuranceInformation.insuranceProvider')}
                  placeholder="e.g., VHI, Laya Healthcare, GMS"
                />
              </div>

              <div className="form-group">
                <label htmlFor="policyNumber">Policy Number</label>
                <input
                  id="policyNumber"
                  type="text"
                  {...register('insuranceInformation.policyNumber')}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label htmlFor="groupNumber">Group Number</label>
                <input
                  id="groupNumber"
                  type="text"
                  {...register('insuranceInformation.groupNumber')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="authorizationNumber">Authorization Number</label>
                <input
                  id="authorizationNumber"
                  type="text"
                  {...register('insuranceInformation.authorizationNumber')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input
                  id="expiryDate"
                  type="date"
                  {...register('insuranceInformation.expiryDate')}
                />
              </div>
            </div>
          </div>
        )
      case 'follow-up':
        return (
          <div className="referral-form-section">
            <div className="form-row form-row-2">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    {...register('followUpRequired')}
                  />
                  Follow-up Required
                </label>
              </div>

              {watch('followUpRequired') && (
                <div className="form-group">
                  <label htmlFor="followUpDate">Follow-up Date</label>
                  <input
                    id="followUpDate"
                    type="date"
                    {...register('followUpDate')}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="notes">Additional Notes</label>
              <textarea
                id="notes"
                {...register('notes')}
                rows={4}
                placeholder="Any additional notes or instructions"
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const sections: AccordionSectionConfig[] = [
    {
      id: 'provider',
      title: 'Provider Information',
      initiallyOpen: true,
    },
    {
      id: 'referral-details',
      title: 'Referral Details',
      initiallyOpen: true,
    },
    {
      id: 'clinical-details',
      title: 'Clinical Details',
    },
    {
      id: 'insurance',
      title: 'Insurance Information',
    },
    {
      id: 'follow-up',
      title: 'Follow-up',
    },
  ]

  return (
    <form className="referral-form" onSubmit={handleSubmit(onSubmit)}>
      <Accordion sections={sections} renderSection={renderSection} />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : isEditing ? 'Update Referral' : 'Create Referral'}
        </button>
      </div>
    </form>
  )
}
